import { useEffect, useState, useCallback } from 'react';
import Cookies from 'universal-cookie';
import { useBucket } from '@context/bucket.context';
import usePageType from '@hooks/use-page-type';
import { AppCookies } from '@enums/cookies';
import useUser from '@hooks/user/use-user';
import { usePreferences } from '@context/preferences.context';
import { useRouter } from 'next/router';
import { getRegionIdByRegionName } from '@helpers/localization';
import { isPricingCalculatorPage, isWebview } from '@helpers/routing';
import { useDeviceSize } from '@context/device-size.context';
import { DeviceType } from '@enums/deviceType';
import { getVisitorIdentityFromCookie } from '@helpers/utils/visitor-identity-cookie';
import { VisitorType } from '@enums/visitor-identity';

const cookies = new Cookies();
const ALLOWED_PAGETYPES = ['HomePage', 'CategoryPage', 'ProductPage', 'HeroPDPPage'];

const useSmartBannerV2FlagGate = () => {
  const { anonymousId } = useBucket();
  const { user } = useUser();
  const { pageType } = usePageType();
  const { siteId } = usePreferences();
  const { pathname, query } = useRouter();
  const isPCPage = isPricingCalculatorPage(pathname);
  const { deviceType } = useDeviceSize();
  const isWebView = isWebview(query);
  const isKr = siteId === getRegionIdByRegionName('KR');
  const getVisitorIdentity = getVisitorIdentityFromCookie();
  const userId = user?.id || anonymousId;

  // TODO: TEST IF ITS OK????
  const isNaverTraffic = query.utm_medium === 'naver_shopping' && query.utm_source === 'naver';
  // Hide smart banner on Pricing Calculator mWeb (isPCpage)
  const hideSmartBanner = deviceType === DeviceType.DESKTOP || (isKr && isNaverTraffic) || isPCPage || isWebView;

  const [hasBigBannerCookie, setHasBigBannerCookie] = useState<boolean | null>(null);
  const [hasSmallBannerCookie, setHasSmallBannerCookie] = useState<boolean | null>(null);

  const isOnAllowedPage = ALLOWED_PAGETYPES.includes(pageType);

  useEffect(() => {
    setHasBigBannerCookie(!!cookies.get(AppCookies.BIG_SMART_BANNER_CLOSED));
    setHasSmallBannerCookie(!!cookies.get(AppCookies.SMALL_SMART_BANNER_CLOSED));
  }, []);

  const shouldShowSmartBannerV2 = useCallback(() => {
    if (hasBigBannerCookie === null || hasSmallBannerCookie === null) {
      return false;
    }

    const hasSeenNoBanners = !hasBigBannerCookie && !hasSmallBannerCookie;
    const hasSeenAll = hasBigBannerCookie && hasSmallBannerCookie;
    const hasSeenBigOnly = hasBigBannerCookie && !hasSmallBannerCookie;
    const isUserLegit = getVisitorIdentity?.visitorType === VisitorType.NormalUser;
    const canShowBanner =
      isUserLegit && !hideSmartBanner && isOnAllowedPage && hasSeenNoBanners && !hasSeenAll && !hasSeenBigOnly;

    if (!canShowBanner) {
      return false;
    }

    // Feature flag evaluation has been removed; we only rely on the above conditions.
    return true;
  }, [userId, hideSmartBanner, hasBigBannerCookie, hasSmallBannerCookie, isOnAllowedPage]);

  return {
    shouldShowSmartBannerV2: shouldShowSmartBannerV2(),
    isOnAllowedPage,
    hasBigBannerCookie,
    hasSmallBannerCookie,
  };
};

export default useSmartBannerV2FlagGate;
